import React, { useCallback, useEffect, useRef, useState } from 'react';
import { getIn } from 'formik';
import { observer } from 'mobx-react';
import { debounce } from 'lodash-es';

import useSession from 'core/hooks/use-session';
import { useStore } from 'core/store';
import { useQueryParams } from 'core/hooks/useQueryParams';
import { formattedAffinityOptions } from 'core/helpers/lookups';
import { TextField } from 'core/components/text-field';
import { Autocomplete } from 'core/components/autocomplete';
import { FieldElementPropTypes } from '../propTypes';

const AffinityAutocomplete = ({
  field: { id, name, label, mode, value, ...props },
  ctx: { setFieldValue, touched, errors, setFieldTouched }
}) => {
  const { affinityLookups } = useStore();
  const abortControllerRef = useRef(null);
  const queryParams = useQueryParams();
  const [inputValue, setInputValue] = useState(queryParams.affinity ?? '');
  const { canWriteMultipleAffinityCodes, user } = useSession();

  const debouncedGetAffinities = debounce((value, signal) => {
    affinityLookups.getSuggestedAffinities({
      queryParams: { affinity: value, name: value },
      requestOptions: { signal },
      excludeDisabled: true,
      limitedList: canWriteMultipleAffinityCodes ? user.viewableAffinityCodes : false
    });
  }, 600);

  const getAffinities = useCallback(
    (affinity) => {
      // abortControllerRef holds reference to the latest fired request.
      // If it exists, cancel it before firing a new request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      debouncedGetAffinities(affinity, abortController.signal);
    },
    [debouncedGetAffinities]
  );

  // function thats fired when someone selects an option from the dropdown
  const handleSelection = useCallback(
    (_, val) => {
      setInputValue('');
      setFieldTouched(id, true, false);
      setFieldValue(id, val?.id || '');
    },
    [setFieldValue, setFieldTouched, id]
  );

  // function that fires when someone is typing in field to search for options
  const onChange = useCallback(
    (event) => {
      const {
        target: { value }
      } = event;
      setInputValue(value);
      getAffinities(value);
    },
    [getAffinities]
  );

  const formattedOptions = formattedAffinityOptions(affinityLookups);

  const currentValue = inputValue
    ? { id: inputValue, text: inputValue }
    : formattedOptions.find((option) => option.id === value);

  useEffect(() => {
    // If there is an affinity in the query params, pre-select it
    if (queryParams.affinity) {
      handleSelection(null, { id: queryParams.affinity.trim() });
    }

    // Cancel any scheduled execution on component unmount
    return () => {
      debouncedGetAffinities.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Autocomplete
      value={currentValue}
      inputValue={inputValue}
      name={name}
      id={id}
      options={formattedOptions}
      onBlur={() => {
        setInputValue('');
      }}
      loading={affinityLookups.loading || affinityLookups.partialLoading}
      onSelection={handleSelection}
      InputComponent={TextField}
      onInputChange={(_, newInputValue) => {
        setInputValue(newInputValue);
      }}
      inputProps={{
        label,
        mode,
        onChange,
        error: getIn(touched, name || id) && !!getIn(errors, name || id),
        helperText: getIn(touched, name || id) ? getIn(errors, name || id) : undefined
      }}
      {...props}
    />
  );
};

AffinityAutocomplete.propTypes = FieldElementPropTypes;

export default observer(AffinityAutocomplete);
